import { motion } from "framer-motion";
import { Hand, ShieldCheck, Sprout, Heart } from "lucide-react";

const PILLARS = [
  {
    icon: Hand,
    title: "Handpicked Quality",
    desc: "Every makhana is sorted by hand to ensure only the plumpest, crunchiest seeds make it into your pouch.",
  },
  {
    icon: ShieldCheck,
    title: "Hygienic Packaging",
    desc: "FSSAI-certified facilities, food-grade resealable pouches and stringent quality checks at every step.",
  },
  {
    icon: Sprout,
    title: "Premium Sourcing",
    desc: "Farm-to-pack from the heartlands of Bihar — the world&rsquo;s finest makhana belt.",
  },
  {
    icon: Heart,
    title: "Healthy Lifestyle",
    desc: "A snack that loves your body back. Light on calories, heavy on goodness.",
  },
];

export default function WhyAhaario() {
  return (
    <section
      id="why"
      className="relative py-24 md:py-32 bg-white"
      data-testid="why-ahaario-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 lg:sticky lg:top-32 self-start">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#3E7D2B]">
              Why Ahaario
            </p>
            <h2 className="font-display font-black text-[#13240D] mt-4 text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Crafted with{" "}
              <span className="text-[#3E7D2B]">care</span>.
              <br />
              Made for{" "}
              <span className="italic">real life.</span>
            </h2>
            <p className="mt-6 text-lg text-[#4A5D44] leading-relaxed max-w-xl">
              We started Ahaario with one simple belief — that snacking should
              never come at the cost of your health. Every pouch we ship carries
              that promise.
            </p>

            <div className="mt-10 flex items-center gap-6 p-5 rounded-3xl bg-[#F9F9F6] border border-[#E8EDE6] max-w-md">
              <div className="h-14 w-14 rounded-2xl bg-[#3E7D2B] flex items-center justify-center text-white font-display font-black text-xl">
                A
              </div>
              <div>
                <p className="font-display font-extrabold text-[#13240D] text-lg">
                  Made in India
                </p>
                <p className="text-sm text-[#4A5D44]">
                  FSSAI Certified • Vegan • Gluten-free
                </p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 0.6, delay: i * 0.08 }}
                  whileHover={{ y: -4 }}
                  className="group relative bg-white rounded-3xl p-7 border border-[#E8EDE6] shadow-[0_8px_30px_rgba(0,0,0,0.03)] hover:shadow-[0_18px_40px_rgba(62,125,43,0.12)] transition-all duration-500"
                  data-testid={`pillar-card-${i}`}
                >
                  <div className="h-12 w-12 rounded-2xl bg-[#A7D129]/25 flex items-center justify-center text-[#3E7D2B] group-hover:bg-[#3E7D2B] group-hover:text-white transition-all duration-500">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="font-display font-extrabold text-[#13240D] mt-6 text-xl md:text-2xl">
                    {p.title}
                  </h3>
                  <p
                    className="mt-3 text-sm md:text-base text-[#4A5D44] leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: p.desc }}
                  />
                  <div className="absolute top-6 right-6 font-display font-black text-[#E8EDE6] text-3xl">
                    0{i + 1}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
